import { h, setChildren } from "../core/dom.js";
import { api } from "../api.js";
import { loadMuscles, muscles, invalidateLibrary } from "../store.js";
import { navigate } from "../core/router.js";
import { backLink } from "../components/common.js";
import { spinner, errorState, toast } from "../components/ui.js";

// Standard option sets from the exercise dataset (kept as the catalog's vocabulary so
// user-added exercises filter/group alongside the seeded ones).
const FORCE = ["push", "pull", "static"];
const LEVEL = ["beginner", "intermediate", "expert"];
const MECHANIC = ["compound", "isolation"];
const CATEGORY = ["strength", "stretching", "plyometrics", "powerlifting", "strongman", "cardio", "olympic weightlifting"];
// How the live logger records this exercise: friendly label → stored metric_type.
const METRIC_TYPES = [
  ["weight_reps", "Weight & reps"],
  ["bodyweight_reps", "Reps only (bodyweight)"],
  ["duration", "Time / hold (stretch, plank)"],
  ["distance_duration", "Distance & time (cardio)"],
];
const EQUIPMENT = ["barbell", "dumbbell", "kettlebells", "cable", "machine", "body only", "bands", "medicine ball", "exercise ball", "foam roll", "e-z curl bar", "other"];

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

export default function ExerciseEditor(params) {
  const root = h("div", { class: "screen" });
  // When given an id we're editing an existing library exercise (admins can edit any entry,
  // including the seeded catalog; a member can edit only their own). Otherwise we're creating.
  const editId = params?.id ? Number(params.id) : null;
  const editing = editId != null;
  // Selection lives in two Sets of muscle_id; a muscle is either primary or secondary.
  const data = { name: "", short_description: "", equipment: "", level: "", mechanic: "", force: "", category: "", metric_type: "weight_reps", instructions: "", image_url: "", image_url_2: "", video_url: "" };
  const primary = new Set();
  const secondary = new Set();
  let saving = false;

  load();
  async function load() {
    setChildren(root, backLink("Exercises", "/library"), spinner());
    try {
      await loadMuscles();
      if (editing) prefill(await api.exercise(editId));
      render();
    } catch (e) {
      setChildren(root, backLink("Exercises", "/library"), errorState(e.message, load));
    }
  }

  // Seed the form from an existing exercise (edit mode). Images map back to the two URL fields;
  // instructions (array of steps) flatten to the one-step-per-line textarea.
  function prefill(ex) {
    data.name = ex.exercise_name || "";
    data.short_description = ex.short_description || "";
    data.equipment = ex.equipment || "";
    data.level = ex.level || "";
    data.mechanic = ex.mechanic || "";
    data.force = ex.force || "";
    data.category = ex.category || "";
    data.metric_type = ex.metric_type || "weight_reps";
    data.instructions = (ex.instructions || []).join("\n");
    data.image_url = (ex.images || [])[0] || "";
    data.image_url_2 = (ex.images || [])[1] || "";
    data.video_url = ex.video_url || "";
    (ex.primary_muscle_ids || []).forEach((id) => primary.add(id));
    (ex.secondary_muscle_ids || []).forEach((id) => secondary.add(id));
  }

  function field(label, control, hint) {
    return h("div", { class: "field" },
      h("label", null, label),
      control,
      hint ? h("div", { class: "dim", style: { fontSize: ".74rem", marginTop: "4px" } }, hint) : null);
  }

  function textInput(key, placeholder) {
    return h("input", { class: "input", value: data[key], placeholder: placeholder || "",
      oninput: (e) => { data[key] = e.target.value; } });
  }

  function select(key, options) {
    return h("select", { class: "select", style: { width: "100%" }, onchange: (e) => { data[key] = e.target.value; } },
      h("option", { value: "" }, "—"),
      ...options.map((o) => h("option", { value: o, selected: data[key] === o }, cap(o))));
  }

  // Mutually-exclusive muscle chips: tapping cycles off → primary → secondary → off.
  // Mutate the existing chip node in place (don't rebuild on tap — see StarRating note).
  function muscleChips() {
    const wrap = h("div", { class: "chip-row", style: { marginTop: "6px" } });
    setChildren(wrap, ...muscles().map((m) => {
      const chip = h("button", { type: "button", class: "chip",
        onClick: () => { cycle(m.muscle_id); paintChip(chip, m.muscle_id, m.muscle); } });
      paintChip(chip, m.muscle_id, m.muscle);
      return chip;
    }));
    return wrap;
  }

  function paintChip(chip, id, name) {
    const isP = primary.has(id), isS = secondary.has(id);
    chip.className = "chip" + (isP || isS ? " on" : "");
    chip.style.background = isS ? "var(--surface-2)" : "";
    chip.style.color = isS ? "var(--text-2)" : "";
    chip.style.borderColor = isS ? "var(--accent)" : "";
    chip.textContent = name + (isP ? " · primary" : isS ? " · secondary" : "");
  }

  function cycle(id) {
    if (primary.has(id)) { primary.delete(id); secondary.add(id); }
    else if (secondary.has(id)) { secondary.delete(id); }
    else { primary.add(id); }
  }

  function render() {
    setChildren(root,
      backLink("Exercises", editing ? `/exercise/${editId}` : "/library"),
      h("h1", { style: { margin: "4px 0 4px" } }, editing ? "Edit exercise" : "New exercise"),
      h("p", { class: "dim", style: { marginTop: 0 } },
        editing
          ? "Changes apply to this shared library exercise for everyone."
          : "Added to the shared library for everyone. You'll be credited as the creator."),

      field("Name", textInput("name", "e.g. Cable Pull-Through")),
      field("Short description", textInput("short_description", "One-line summary (optional)")),

      field("How it's logged",
        h("select", { class: "select", style: { width: "100%" }, onchange: (e) => { data.metric_type = e.target.value; } },
          ...METRIC_TYPES.map(([v, label]) => h("option", { value: v, selected: data.metric_type === v }, label))),
        "Picks the live logger's inputs — weight/reps, reps only, a timed hold, or distance + time."),

      h("div", { style: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 12px" } },
        field("Equipment", select("equipment", EQUIPMENT)),
        field("Category", select("category", CATEGORY)),
        field("Level", select("level", LEVEL)),
        field("Mechanic", select("mechanic", MECHANIC)),
        field("Force", select("force", FORCE))
      ),

      field("Target muscles",
        muscleChips(),
        "Tap once for primary, again for secondary, again to clear."),

      field("Instructions",
        h("textarea", { class: "input", rows: 5, placeholder: "One step per line", style: { resize: "vertical" },
          oninput: (e) => { data.instructions = e.target.value; } }, data.instructions),
        "Each line becomes a numbered step."),

      field("Start position image URL",
        textInput("image_url", "https://i.imgur.com/…"),
        "Optional. Paste a link (Imgur, Cloudinary, Bunny, GitHub, postimg, ibb, Unsplash, Wikimedia). Never uploaded — link only."),

      field("Finish position image URL",
        textInput("image_url_2", "https://i.imgur.com/…"),
        "Optional. The second photo (end of the movement), like the seeded exercises."),

      field("Demo video URL",
        textInput("video_url", "https://youtu.be/…"),
        "Optional. YouTube, Vimeo, Bunny, Streamable or Loom link."),

      h("button", { class: "btn btn-primary btn-block mt", onClick: save }, editing ? "Save changes" : "Create exercise")
    );
  }

  async function save() {
    if (saving) return;
    if (!data.name.trim()) { toast("Give the exercise a name", "error"); return; }
    saving = true;
    const payload = {
      exercise_name: data.name,
      short_description: data.short_description,
      equipment: data.equipment, level: data.level, mechanic: data.mechanic,
      force: data.force, category: data.category, metric_type: data.metric_type,
      instructions: data.instructions,
      image_url: data.image_url, image_url_2: data.image_url_2, video_url: data.video_url,
      primary_muscle_ids: [...primary],
      secondary_muscle_ids: [...secondary],
    };
    try {
      const ex = editing ? await api.updateExercise(editId, payload) : await api.createExercise(payload);
      invalidateLibrary();
      toast(editing ? "Exercise updated" : "Exercise added to the library", "success");
      navigate(`/exercise/${ex.id}`);
    } catch (e) {
      saving = false;
      toast(e.message, "error");
    }
  }

  return root;
}
